/**
 * Spielplan mit wechselnden Partnern (Harald, 10/2026; im Padel „Americano").
 * Aus einer Gruppe Einzelspieler werden Spiele Doppel gegen Doppel:
 *
 * - `once`: jedes Paar spielt genau einmal zusammen. Bei 5 Spielern sind das
 *   die 10 möglichen Paare in 5 Spielen — Haralds Plan C/D–E/B, D/E–A/C …;
 *   jeder spielt 4-mal und setzt einmal aus. Glatt geht das nur, wenn die
 *   Zahl der Paare gerade ist (4, 5, 8, 9 … Spieler); sonst bleibt ein Paar
 *   übrig, das nicht zusammen spielt (`unpaired`).
 * - `all`: jedes Paar gegen jedes andere Paar — „jeder mit jedem gegen jeden
 *   mit jedem". 4 Spieler → 3 Spiele, 5 → 15, 6 → 45.
 *
 * Die Reihenfolge ist so gewählt, dass möglichst wenige Spieler zweimal
 * hintereinander dran sind und — ab 8 Spielern — zwei aufeinanderfolgende
 * Spiele gleichzeitig auf zwei Feldern laufen können. Deterministisch.
 */

export interface PartnerPairing<T> {
  /** Laufende Runde in der Gruppe (1-basiert) */
  round: number;
  a: [T, T];
  b: [T, T];
}

export interface PartnerPlan<T> {
  matches: PartnerPairing<T>[];
  /** Paare, die nie zusammen spielen (nur `once` bei ungerader Paarzahl) */
  unpaired: [T, T][];
}

type Paar = [number, number];
type Spiel = [Paar, Paar];

/** Höchstzahl Suchschritte, bevor `once` auf die gierige Lösung zurückfällt */
const MAX_SCHRITTE = 200_000;

export function partnerSchedule<T>(players: readonly T[], mode: 'once' | 'all'): PartnerPlan<T> {
  const n = players.length;
  if (n < 4) return { matches: [], unpaired: [] };
  const paare: Paar[] = [];
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) paare.push([i, j]);

  let spiele: Spiel[];
  let rest: Paar[] = [];
  if (mode === 'all') {
    spiele = [];
    for (let i = 0; i < n; i++)
      for (let j = i + 1; j < n; j++)
        for (let k = j + 1; k < n; k++)
          for (let l = k + 1; l < n; l++)
            spiele.push(
              [
                [i, j],
                [k, l],
              ],
              [
                [i, k],
                [j, l],
              ],
              [
                [i, l],
                [j, k],
              ],
            );
  } else {
    ({ spiele, rest } = jedesPaarEinmal(n, paare));
  }

  const p = (x: Paar): [T, T] => [players[x[0]] as T, players[x[1]] as T];
  return {
    matches: reihenfolge(n, spiele).map((s, i) => ({ round: i + 1, a: p(s[0]), b: p(s[1]) })),
    unpaired: rest.map(p),
  };
}

/**
 * Gruppen bei wechselnden Partnern: Die Gruppengröße ist eine **Untergrenze**.
 * 11 Spieler bei Größe 5 → 6 + 5, nicht 4 + 4 + 3 wie sonst — eine Gruppe
 * unter 4 hätte gar keine Spiele (Daniel, 10/2026: „man wählt doch
 * Gruppengröße 5, trotzdem macht er andere Gruppen“).
 */
export function partnerLayoutSpec(n: number, groupSize: number): { groupCount: number } {
  return { groupCount: Math.max(1, Math.floor(n / Math.max(4, groupSize))) };
}

/** Anzahl Spiele je Gruppe — für die Vorschau im Assistenten */
export function partnerMatchCount(n: number, mode: 'once' | 'all'): number {
  if (n < 4) return 0;
  const paare = (n * (n - 1)) / 2;
  return mode === 'all' ? (paare * ((n - 2) * (n - 3))) / 2 / 2 : Math.floor(paare / 2);
}

const getrennt = (x: Paar, y: Paar) => x[0] !== y[0] && x[0] !== y[1] && x[1] !== y[0] && x[1] !== y[1];

/**
 * Alle Paare in Spiele aus zwei getrennten Paaren aufteilen (Rückverfolgung).
 * Unter den möglichen Gegnern zuerst die, gegen die man bisher am seltensten
 * spielt — so trifft bei 5 Spielern jeder genau zweimal auf jeden.
 */
function jedesPaarEinmal(n: number, paare: Paar[]): { spiele: Spiel[]; rest: Paar[] } {
  const benutzt = paare.map(() => false);
  const gegen = Array.from({ length: n }, () => new Array<number>(n).fill(0));
  const spiele: Spiel[] = [];
  const rest: Paar[] = [];
  let schritte = 0;
  const kosten = (x: Paar, y: Paar) => (gegen[x[0]]?.[y[0]] ?? 0) + (gegen[x[0]]?.[y[1]] ?? 0) + (gegen[x[1]]?.[y[0]] ?? 0) + (gegen[x[1]]?.[y[1]] ?? 0);
  const zaehle = (x: Paar, y: Paar, d: number) => {
    for (const u of x)
      for (const v of y) {
        (gegen[u] as number[])[v] = (gegen[u]?.[v] ?? 0) + d;
        (gegen[v] as number[])[u] = (gegen[v]?.[u] ?? 0) + d;
      }
  };

  const suche = (uebrig: number): boolean => {
    if (++schritte > MAX_SCHRITTE) return false;
    const f = benutzt.indexOf(false);
    if (f < 0) return true;
    const x = paare[f] as Paar;
    benutzt[f] = true;
    const kandidaten = paare
      .map((y, g) => ({ y, g }))
      .filter(({ y, g }) => !benutzt[g] && getrennt(x, y))
      .sort((u, v) => kosten(x, u.y) - kosten(x, v.y) || u.g - v.g);
    for (const { y, g } of kandidaten) {
      benutzt[g] = true;
      spiele.push([x, y]);
      zaehle(x, y, 1);
      if (suche(uebrig)) return true;
      zaehle(x, y, -1);
      spiele.pop();
      benutzt[g] = false;
    }
    if (uebrig > 0) {
      rest.push(x);
      if (suche(uebrig - 1)) return true;
      rest.pop();
    }
    benutzt[f] = false;
    return false;
  };

  if (suche(paare.length % 2)) return { spiele, rest };

  // Zu viele Möglichkeiten (große Gruppe): gierig, übrige Paare bleiben übrig
  benutzt.fill(false);
  spiele.length = 0;
  rest.length = 0;
  paare.forEach((x, f) => {
    if (benutzt[f]) return;
    benutzt[f] = true;
    const g = paare.findIndex((y, k) => !benutzt[k] && getrennt(x, y));
    if (g < 0) rest.push(x);
    else {
      benutzt[g] = true;
      spiele.push([x, paare[g] as Paar]);
    }
  });
  return { spiele, rest };
}

/**
 * Reihenfolge: als Nächstes das Spiel, dessen Spieler zuletzt am wenigsten
 * dran waren — erst wer im Spiel davor stand, dann wer im vorletzten, dann wer
 * insgesamt schon mehr gespielt hat.
 */
function reihenfolge(n: number, spiele: Spiel[]): Spiel[] {
  const offen = spiele.slice();
  const gespielt = new Array<number>(n).fill(0);
  const out: Spiel[] = [];
  let vorher = new Set<number>();
  let vorvorher = new Set<number>();
  while (offen.length > 0) {
    let best = 0;
    let bestKosten = Infinity;
    offen.forEach((s, i) => {
      const leute = [...s[0], ...s[1]];
      const k = leute.reduce((sum, p) => sum + (vorher.has(p) ? 1000 : 0) + (vorvorher.has(p) ? 100 : 0) + (gespielt[p] ?? 0), 0);
      if (k < bestKosten) {
        bestKosten = k;
        best = i;
      }
    });
    const s = offen.splice(best, 1)[0] as Spiel;
    out.push(s);
    const leute = [...s[0], ...s[1]];
    for (const p of leute) gespielt[p] = (gespielt[p] ?? 0) + 1;
    vorvorher = vorher;
    vorher = new Set(leute);
  }
  return out;
}
